/**
 * Shared formatting helpers used across pages/components.
 */

/**
 * Format a numeric amount as a localized currency string.
 * Falls back to a `"NN.NN CUR"` form if the currency code is unknown to
 * Intl.NumberFormat (defensive — hand-typed / test-only codes).
 *
 * Extracted from src/pages/costsTabPie.ts (2026-08-31) so pages other
 * than the costs tab can format money without a semantic import from a
 * pie-chart module. The behavior is byte-identical.
 */
export function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Human-friendly relative age: `just now` / `Xm ago` / `Xh ago` /
 * `X day/days ago`. Extracted from src/pages/PipelineTab.tsx (2026-08-31)
 * — the day-suffix uses `day`/`days` singular-plural (not `Xd`) so
 * PipelineTab's existing popover copy stays byte-identical.
 *
 * Takes an ISO string (both live callers — PipelineTab, SummarisePanel —
 * receive `generated_at` / `authored_at` as ISO from the wire). Returns
 * the raw input on parse failure so the caller never sees an exception.
 * If a Date-typed caller ever appears, convert at the call site with
 * `.toISOString()` — narrower here avoids the `Invalid Date.toISOString()`
 * RangeError trap that the previous `string | Date` union exposed.
 *
 * `now` defaults to `new Date()`; tests inject a fixed date for
 * determinism.
 */
export function formatRelativeAge(when: string, now: Date = new Date()): string {
  const then = new Date(when);
  const thenMs = then.getTime();
  if (Number.isNaN(thenMs)) return when;
  const diffMs = now.getTime() - thenMs;
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  const days = Math.floor(diffMs / 86_400_000);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ----------------------------------------------------------------------------
// Analytics-v2 widget helpers (extracted from FunnelWidget / CampaignsWidget
// as part of CORE-1041).
//
// null carries "no signal" / "not applicable" semantics — must render as a
// distinct sentinel from zero, never coerce silently.
// ----------------------------------------------------------------------------

/** Localised integer, or `—` when the value is null (source silent / N/A). */
export function formatCount(value: number | null): string {
  return value === null ? '—' : value.toLocaleString();
}

/** `NN.N%`, or `—` when the value is null (no previous stage / structural absence). */
export function formatPercent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

/**
 * `NN.N%`, or `N/A` when the value is null.
 *
 * Distinct from formatPercent: `null` in a reconciliation delta means the
 * denominator was zero (no meaningful ratio), NOT "in alignment" — see
 * FunnelReconciliation.deltaPercent type doc.
 */
export function formatDelta(value: number | null): string {
  return value === null ? 'N/A' : `${value.toFixed(1)}%`;
}

/**
 * Format a 0-1 decimal ratio as a `NN.N%` display string. Distinct from
 * `formatPercent`, which expects a value already in percent form (e.g. `31.2`).
 *
 * Backend analytics-v2 fields store ratios at 4dp precision (conversionRate,
 * bounceRate); this helper multiplies by 100 and rounds to 1dp for display.
 * Null carries "no meaningful denominator" — renders as `—`, never `0.0%`.
 */
export function formatRatioAsPercent(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

/**
 * Format an ISO 8601 timestamp via `Date.prototype.toLocaleString()`. Returns
 * the raw input verbatim when the string can't be parsed as a valid Date
 * (Number.isNaN on getTime() is the canonical Invalid Date check;
 * toLocaleString itself never throws — it renders "Invalid Date" as text,
 * which is worse than the raw input).
 *
 * Optional Intl.DateTimeFormatOptions second arg is forwarded to
 * toLocaleString unchanged. Common values across the app:
 *   - `{ dateStyle: 'medium', timeStyle: 'short' }` — page-level "last updated" lines
 *   - `{ dateStyle: 'short',  timeStyle: 'short' }` — dense table cells
 *
 * Extracted from ~14 duplicate copies of the same "try new Date(iso)
 * .toLocaleString() catch return iso" pattern across widget files
 * (CORE-961 R1/R2). The try/catch shape was defensive dead weight; this
 * matches formatRelativeAge's Number.isNaN guard.
 */
export function formatDateTime(iso: string, options?: Intl.DateTimeFormatOptions): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, options);
}

/**
 * Signed percentage with explicit sign prefix. `+3.5%` on positive,
 * `−3.5%` (U+2212 MINUS SIGN, not ASCII hyphen) on negative, `0.0%` on
 * real zero (no sign), `—` on null. Rounded to 1dp.
 *
 * Distinct from `formatDelta` and `formatPercent` — this one is for
 * reconciliation-style deltas where the sign is load-bearing (source
 * over- vs under-reports). U+2212 renders slightly wider than the ASCII
 * hyphen and reads more clearly in dense numeric columns.
 */
export function formatSignedPercent(value: number | null): string {
  if (value === null) return '—';
  const rounded = Number(value.toFixed(1));
  if (rounded > 0) return `+${rounded.toFixed(1)}%`;
  if (rounded < 0) return `−${Math.abs(rounded).toFixed(1)}%`;
  return '0.0%';
}
